import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useStyles } from './SearchResultsLayout.styles';
import { FilterState } from '../../helpers/filters';
import FiltersPills from '../Filters/FiltersPills';
import EnhancedFilters from '../Filters/EnhancedFilters';
import MobileFilters from '../Filters/MobileFilters';
import MobileFiltersButton from '../Filters/MobileFiltersButton';
import { FiltersLists } from '../../services/business-registry';
import { BreadcrumbItem } from '../Breadcrumbs/BreadcrumbContainer';

export interface SearchResultsLayoutProps {
  loading: boolean;
  header: React.ReactNode;
  resultsCountRadiusSelectorContainer: React.ReactNode;
  noResultsContainer: React.ReactNode;
  sortSelector: React.ReactNode;
  searchResultsContainer: React.ReactNode;
  useFilterHandlers: FilterState;
  searchResultsCount: number;
  filtersLists: FiltersLists;
  breadcrumbs: Array<BreadcrumbItem>;
}

export default function SearchResultsLayout({
  loading,
  header,
  resultsCountRadiusSelectorContainer,
  noResultsContainer,
  sortSelector,
  searchResultsContainer,
  useFilterHandlers,
  searchResultsCount,
  filtersLists,
  breadcrumbs,
}: SearchResultsLayoutProps) {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const classes = useStyles();
  const { filters } = useFilterHandlers;
  const numFiltersSelected = [
    ...filters.constructionSector,
    ...filters.companyType,
    ...filters.classifications,
    ...filters.trade,
  ].length;
  const radiusEnabled = !filters.radius.disabled;

  return (
    <Box display="flex" maxWidth="1780px" className={classes.root}>
      <Box className={classes.mobileFilters}>
        <MobileFilters
          data-test-id="mobile-filters"
          useFilterHandlers={useFilterHandlers}
          loading={loading}
          searchResultsCount={searchResultsCount}
          closeFilters={() => {
            setShowMobileFilters(false);
          }}
          open={showMobileFilters}
          filtersLists={filtersLists}
          breadcrumbs={breadcrumbs}
        />
      </Box>
      <Box mx={2} width="100%" className={classes.container}>
        <Grid container>
          <Grid item xs={12}>
            <Box pr={3}>{header}</Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              marginBottom="13px"
              marginTop="13px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid container alignItems="flex-end">
                <Grid item xs={8} sm={8} md={8} lg={3}>
                  {resultsCountRadiusSelectorContainer}
                </Grid>
                <Grid
                  item
                  xs={4}
                  sm={4}
                  md={4}
                  lg={9}
                  className={classes.desktopFilters}
                >
                  <Box display="flex" justifyContent="space-between">
                    <Grid container>
                      <Grid
                        item
                        xs={radiusEnabled ? 10 : 12}
                        className={classes.filterPillsWrapper}
                      >
                        <Box>
                          <FiltersPills
                            useFilterHandlers={useFilterHandlers}
                            loading={loading}
                          />
                        </Box>
                      </Grid>

                      {radiusEnabled && (
                        <Grid item xs={2} style={{ alignSelf: 'flex-end' }}>
                          {sortSelector}
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Box className={classes.mobileFilters}>
            <Grid item xs={12}>
              <Box display="flex" mb={2}>
                <MobileFiltersButton
                  handleClick={() => {
                    setShowMobileFilters(!showMobileFilters);
                  }}
                  numFiltersSelected={numFiltersSelected}
                />
                {sortSelector}
              </Box>
            </Grid>
          </Box>

          <Grid item xs={12}>
            <Grid container>
              <Grid item lg={3} className={classes.desktopFilters}>
                <EnhancedFilters
                  data-test-id="enhanced-filters"
                  useFilterHandlers={useFilterHandlers}
                  filtersLists={filtersLists}
                  breadcrumbs={breadcrumbs}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={9}
                id="searchResultsContainerWrapper"
              >
                {noResultsContainer}
                {searchResultsContainer}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
